import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";

let stripeLoader;
function loadStripe() {
    if (window.Stripe) return Promise.resolve(window.Stripe);
    if (!stripeLoader) stripeLoader = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://js.stripe.com/v3/";
        script.onload = () => resolve(window.Stripe);
        script.onerror = () => { stripeLoader = null; script.remove(); reject(new Error("Secure payment could not load. Please retry.")); };
        document.head.appendChild(script);
    });
    return stripeLoader;
}

export function CartPayment() {
    const [order, setOrder] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [ready, setReady] = useState(false);
    const [useLink, setUseLink] = useState(false);
    const [linkAvailable, setLinkAvailable] = useState(false);
    const target = useRef(null);
    const payment = useRef(null);
    const [key] = useState(() => { try { return sessionStorage.getItem("pout_cart_key"); } catch { return null; } });
    const finish = async () => {
        setBusy(true); setError("");
        try {
            const result = await api.cartComplete(key);
            setOrder(result);
            if (result.state === "complete") sessionStorage.removeItem("pout_cart_request");
            else setError(result.message || "Confirmation is pending. Please check again; do not make another payment.");
        } catch (e) { setError(e.message); }
        finally { setBusy(false); }
    };
    useEffect(() => {
        let active = true;
        if (!key) { setError("This browser has no booking session. Please contact the clinic if you have already paid."); return; }
        const returning = new URLSearchParams(window.location.search).has("payment_intent");
        const call = returning ? api.cartComplete(key) : api.cartSession(key);
        call.then((o) => { if (active) { setOrder(o); if (o.state === "complete") sessionStorage.removeItem("pout_cart_request"); } }).catch((e) => { if (active) setError(e.message); });
        return () => { active = false; };
    }, [key]);
    useEffect(() => {
        if (order?.state !== "awaiting_payment" || !order.client_secret) return;
        let active = true;
        let element;
        setReady(false);
        loadStripe().then(async (Stripe) => {
            if (!active || !target.current) return;
            const stripe = Stripe(order.publishable_key, order.connect_account ? {stripeAccount:order.connect_account} : undefined);
            const checked = await stripe.retrievePaymentIntent(order.client_secret);
            if (!active) return;
            if (checked.error) throw new Error(checked.error.message);
            setLinkAvailable(checked.paymentIntent?.payment_method_types?.includes("link") === true);
            if (checked.paymentIntent?.currency !== "gbp" || checked.paymentIntent?.amount !== Math.round(order.quote.amount_due * 100)) {
                throw new Error("The payment total differs from your quote. Please contact the clinic before paying.");
            }
            if (["succeeded", "processing", "requires_capture"].includes(checked.paymentIntent.status)) {
                setError("Your payment has been submitted. Use ‘Check confirmation’ below to confirm your treatments.");
                return;
            }
            const elements = stripe.elements({clientSecret:order.client_secret, appearance:{theme:"night", variables:{colorPrimary:"#c5a059",colorBackground:"#0e1746"}}});
            element = elements.create("payment", {
                layout: {type: "accordion", defaultCollapsed: !useLink, radios: "always", spacedAccordionItems: true, visibleAccordionItemsCount: 0},
                paymentMethodOrder: useLink ? ["link", "google_pay", "apple_pay", "card", "klarna", "revolut_pay", "amazon_pay"] : ["google_pay", "apple_pay", "card", "klarna", "revolut_pay", "amazon_pay"],
                wallets: {link: useLink ? "auto" : "never", googlePay: "auto", applePay: "auto"},
            });
            element.on("ready", () => { if (active) setReady(true); });
            element.on("loaderror", () => { if (active) setError("The payment form could not load. Refresh to retry."); });
            element.mount(target.current);
            payment.current = {stripe, elements};
        }).catch((e) => { if (active) setError(e.message); });
        return () => { active = false; element?.destroy(); payment.current = null; };
    }, [order?.state, order?.client_secret, order?.publishable_key, order?.connect_account, order?.quote?.amount_due, useLink]);
    const switchPaymentView = () => {
        if (busy || !ready) return;
        setReady(false);
        setError("");
        setUseLink((previous) => !previous);
    };
    const pay = async () => {
        if (busy || !ready || !payment.current) return;
        setBusy(true); setError("");
        try {
            const result = await payment.current.stripe.confirmPayment({elements:payment.current.elements,
                confirmParams:{return_url:`${window.location.origin}/booking/cart-payment`}, redirect:"if_required"});
            if (result.error) { setError(result.error.message); setBusy(false); return; }
            await finish();
        } catch (e) { setError(e.message); setBusy(false); }
    };
    return <main className="min-h-screen bg-navy text-white px-4 py-10"><div className="max-w-lg mx-auto space-y-5">
        <h1 className="font-serif text-3xl">{order?.state === "complete" ? "Your treatments are booked" : "Your booking"}</h1>
        {order?.quote && <section className="rounded-2xl border border-gold/40 p-5 space-y-3">
            {order.quote.items.map((i) => <div key={i.service_id} className="flex justify-between gap-3 text-sm"><span>{i.name} × {i.quantity}</span><span>{formatMoney(i.price)}</span></div>)}
            <p className="flex justify-between border-t border-white/20 pt-3"><span>Treatment total</span><span>{formatMoney(order.quote.price)}</span></p>
            <p className="flex justify-between text-gold"><span>{order.state === "complete" ? "Paid" : "Due today"}</span><span>{formatMoney(order.quote.amount_due)}</span></p>
            {order.state === "complete" && <p className="flex justify-between"><span>Balance at appointment</span><span>{formatMoney(Math.max(0, order.quote.price - order.quote.amount_due))}</span></p>}
            <p className="text-xs text-white/60">Includes each treatment’s deposit, or its full price where no deposit is configured.</p>
        </section>}
        {error && <p role="alert" className="rounded-xl border border-red-400/50 p-4 text-sm text-red-100">{error}</p>}
        {!order && !error && <p>Loading your booking…</p>}
        {order?.state === "awaiting_payment" && <>
            <h2 className="font-serif text-xl">{useLink ? "Pay with Link" : "Choose how to pay"}</h2>
            {useLink && <button type="button" disabled={busy || !ready} onClick={switchPaymentView} className="min-h-11 text-gold underline disabled:opacity-40">Back to all payment methods</button>}
            <div ref={target} />
            {!ready && !error && <p role="status" className="text-sm text-white/70">Loading secure payment options…</p>}
            {!useLink && linkAvailable && <button type="button" disabled={busy || !ready} onClick={switchPaymentView} className="w-full min-h-12 rounded-xl border border-gold/50 px-4 text-gold disabled:opacity-40">Use Link saved payment details</button>}
            <button disabled={busy || !ready} onClick={pay} className="w-full min-h-12 rounded-full bg-gold text-navy font-semibold disabled:opacity-40">{busy ? "Confirming…" : `Pay ${formatMoney(order.quote.amount_due)}`}</button>
            <button disabled={busy} onClick={finish} className="min-h-11 text-gold underline">Already paid? Check confirmation</button>
        </>}
        {order?.state === "complete" && <><p>All treatments are included in your booking.</p>{order.appointments.map((a) => <p key={a.id} className="text-sm">Reference #{a.id} · {a.start}</p>)}
            {order.consultation_link && <a href={order.consultation_link} className="flex min-h-12 items-center justify-center rounded-full bg-gold text-navy font-semibold">Complete your forms</a>}
            {order.management_link && <a href={order.management_link} className="flex min-h-12 items-center justify-center rounded-full border border-gold text-gold">Manage your appointment</a>}
            {order.forms_pending && <p role="status">Your booking is confirmed. Your forms are still being prepared. <button disabled={busy} onClick={finish} className="text-gold underline">Check again</button></p>}
            <a href="/" className="inline-flex min-h-11 items-center text-gold underline">Return to treatments</a></>}
        {order && !["complete","awaiting_payment"].includes(order.state) && <><p>{order.message || "Your booking needs confirmation. Please contact the clinic before starting another checkout."}</p><button disabled={busy} onClick={finish} className="min-h-11 text-gold underline">Check booking status</button></>}
    </div></main>;
}
